<script setup lang="ts">
  import { useIsAiAssistantAppRoute } from '@/composables/useIsAiAssistantAppRoute'
  import { useTranslation } from '@/composables/useTranslation'
  import { Section } from 'lucide-vue-next'
  import { computed } from 'vue'

  /** Data-cy prefix for the prompt chips — shared with the spec so the selector cannot drift. */
  const PROMPT_CY_PREFIX = 'verdicts-intro-prompt-'

  interface PromptSuggestion {
    id: string
    labelKey: string
    promptKey: string
  }

  const PROMPT_SUGGESTIONS: readonly PromptSuggestion[] = [
    { id: 'assess-damages', labelKey: 'AssessDamages', promptKey: 'PromptAssessDamages' },
    { id: 'interpret-clause', labelKey: 'InterpretClause', promptKey: 'PromptInterpretClause' },
    { id: 'review-evidence', labelKey: 'ReviewEvidence', promptKey: 'PromptReviewEvidence' },
    { id: 'breach-of-contract', labelKey: 'BreachOfContract', promptKey: 'PromptBreachOfContract' },
    { id: 'termination-risk', labelKey: 'TerminationRisk', promptKey: 'PromptTerminationRisk' }
  ] as const

  const isAiAssistantAppRoute = useIsAiAssistantAppRoute()

  const emit = defineEmits<{
    promptSelected: [payload: { id: string; prompt: string }]
  }>()

  const { t } = useTranslation({
    en: {
      Title: 'Verdicts are on',
      Line1: 'Verdicts are official court decisions.',
      Line2:
        'With Verdicts enabled, the AI can ground its answers in real legal sources and ' +
        'surface citations or previews of the underlying verdict text.',
      PromptExamplesLabel: 'Verdict prompt examples:',
      AssessDamages: 'Assess damages',
      InterpretClause: 'Interpret clause',
      ReviewEvidence: 'Review evidence',
      BreachOfContract: 'Breach of contract',
      TerminationRisk: 'Termination risk',
      PromptAssessDamages:
        'Based on the relevant verdicts, how should damages be assessed in this case?',
      PromptInterpretClause: 'How have courts interpreted similar clauses in recent verdicts?',
      PromptReviewEvidence:
        'Which verdicts discuss how the evidence in a case like this is reviewed?',
      PromptBreachOfContract:
        'What do recent verdicts say about breach of contract in situations like this?',
      PromptTerminationRisk: 'Based on the case law, what is the termination risk to consider here?'
    },
    de: {
      Title: 'Urteilsdatenbank ist aktiviert',
      Line1: 'Urteile sind offizielle gerichtliche Entscheidungen.',
      Line2:
        'Mit aktivierter Urteilsdatenbank kann die KI ihre Antworten auf echte rechtliche ' +
        'Quellen stützen und Zitate oder Auszüge aus den Urteilen direkt im Chat anzeigen.',
      PromptExamplesLabel: 'Beispiel-Prompts für Urteile:',
      AssessDamages: 'Schadensbewertung',
      InterpretClause: 'Klausel auslegen',
      ReviewEvidence: 'Beweise prüfen',
      BreachOfContract: 'Vertragsverletzung',
      TerminationRisk: 'Kündigungsrisiko',
      PromptAssessDamages:
        'Wie ist auf Basis der einschlägigen Urteile der Schaden in diesem Fall zu bewerten?',
      PromptInterpretClause:
        'Wie haben Gerichte vergleichbare Klauseln in aktuellen Urteilen ausgelegt?',
      PromptReviewEvidence:
        'Welche Urteile behandeln die Prüfung der Beweise in einem vergleichbaren Fall?',
      PromptBreachOfContract:
        'Was sagen aktuelle Urteile zur Vertragsverletzung in vergleichbaren Situationen?',
      PromptTerminationRisk:
        'Wie ist das Kündigungsrisiko hier auf Grundlage der Rechtsprechung einzuschätzen?'
    },
    fr: {
      Title: 'Les décisions de justice sont activées',
      Line1: 'Les décisions de justice sont des décisions officielles des tribunaux.',
      Line2:
        "Avec les décisions de justice activées, l'IA peut fonder ses réponses sur des sources " +
        'juridiques réelles et afficher les citations ou des extraits du texte sous-jacent.',
      PromptExamplesLabel: 'Exemples de prompts sur les décisions de justice :',
      AssessDamages: 'Évaluer les dommages',
      InterpretClause: 'Interpréter une clause',
      ReviewEvidence: 'Examiner les preuves',
      BreachOfContract: 'Rupture de contrat',
      TerminationRisk: 'Risque de résiliation',
      PromptAssessDamages:
        'Sur la base des décisions pertinentes, comment évaluer les dommages dans cette affaire ?',
      PromptInterpretClause:
        'Comment les tribunaux ont-ils interprété des clauses similaires dans des décisions récentes ?',
      PromptReviewEvidence:
        "Quelles décisions traitent de l'examen des preuves dans un cas comparable ?",
      PromptBreachOfContract:
        'Que disent les décisions récentes sur la rupture de contrat dans des situations comparables ?',
      PromptTerminationRisk:
        'Sur la base de la jurisprudence, quel est le risque de résiliation à considérer ici ?'
    }
  })

  const renderedPrompts = computed(() =>
    PROMPT_SUGGESTIONS.map((suggestion) => ({
      id: suggestion.id,
      label: t(suggestion.labelKey),
      prompt: t(suggestion.promptKey)
    }))
  )

  /**
   * Mirrors {@link AiChatAssistantMessageRenderer} so the intro bubble reads as a regular
   * assistant message: plain prose on the fullscreen KI-Assistent route, avatar + bordered
   * card on legacy global/case chat. Kept as computed refs so the (long) Tailwind classes
   * stay out of the template and can be scanned at a glance.
   */
  const wrapperClass = computed(() =>
    isAiAssistantAppRoute.value
      ? 'flex w-full flex-col items-start gap-3 self-stretch'
      : 'flex gap-x-2 sm:gap-x-4'
  )
  const bodyClass = computed(() =>
    isAiAssistantAppRoute.value
      ? 'w-full max-w-2xl space-y-3'
      : 'w-full max-w-[90%] grow space-y-3 md:max-w-2xl'
  )
  const cardClass = computed(() =>
    isAiAssistantAppRoute.value
      ? 'space-y-3 text-sm text-gray-800'
      : 'space-y-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-800'
  )

  const onPromptClick = (id: string, prompt: string) => {
    emit('promptSelected', { id, prompt })
  }
</script>

<template>
  <div
    :class="wrapperClass"
    data-cy="verdicts-intro-message"
  >
    <div
      v-if="!isAiAssistantAppRoute"
      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100"
    >
      <span class="text-xs font-medium text-purple-600">AI</span>
    </div>
    <div :class="bodyClass">
      <div :class="cardClass">
        <p class="font-semibold">{{ t('Title') }}</p>
        <div class="space-y-1">
          <p>{{ t('Line1') }}</p>
          <p>{{ t('Line2') }}</p>
        </div>
        <p class="font-semibold">{{ t('PromptExamplesLabel') }}</p>
        <div class="flex flex-wrap items-start gap-2">
          <button
            v-for="p in renderedPrompts"
            :key="p.id"
            type="button"
            class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs
              font-medium leading-4 text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none
              focus:ring-2 focus:ring-primary focus:ring-offset-1"
            :data-cy="`${PROMPT_CY_PREFIX}${p.id}`"
            @click="onPromptClick(p.id, p.prompt)"
          >
            <Section
              class="h-4 w-4 shrink-0 text-gray-800"
              :stroke-width="1.25"
            />
            <span>{{ p.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
